import { uniqBy } from 'lodash';
import moment from 'moment';
import { ProjectPhase } from 'api/project';
import { GanttBarColors } from 'components/colors';
import { sortByDates } from 'utils/sort-by-dates';
import { ProjectPhaseEvent, SchedulerEventType } from '../../event-types';
import { ProjectPhaseWithStyles } from '../../types';
import { resourceIdForProject } from '../id';
import {
  getSchedulerEventProps,
  getSchedulerEventsClass,
  isOverlapping,
  PHASE_STYLE,
} from './common';

const getColor = (randomNumber: number): string =>
  GanttBarColors[randomNumber % GanttBarColors.length];

export const phaseAsEvent = (
  projectId: number,
  {
    projectPhaseId,
    projectPhaseName,
    dueDate,
    startDate,
    top,
    height,
    groupKey,
    rowIndex,
  }: ProjectPhaseWithStyles,
): ProjectPhaseEvent => ({
  type: SchedulerEventType.ProjectPhase,
  id: `${projectId}_${projectPhaseId}`,
  phaseName: projectPhaseName,
  resourceId: resourceIdForProject(projectId),
  startDate: moment(startDate).startOf('day').toDate(),
  endDate: moment(dueDate).endOf('day').toDate(),
  groupKey,
  rowIndex,
  activePhase: rowIndex === 0,
  top,
  height,
  ...getSchedulerEventProps(SchedulerEventType.ProjectPhase, {
    cls: getSchedulerEventsClass(
      SchedulerEventType.ProjectPhase,
      `${getColor(projectPhaseId)}`,
    ),
  }),
});
const sortPhasesByStartDate = (phases: ProjectPhase[]): ProjectPhase[] =>
  [...phases].sort(({ startDate: a }, { startDate: b }) => sortByDates(a, b));

const isPhaseOverlapping = (phaseA: ProjectPhase, phaseB: ProjectPhase) => {
  const { startDate: startDateA, dueDate: endDateA } = phaseA;
  const { startDate: startDateB, dueDate: endDateB } = phaseB;

  return isOverlapping(
    {
      startDate: moment(startDateA).startOf('day'),
      endDate: moment(endDateA).endOf('day'),
    },
    {
      startDate: moment(startDateB).startOf('day'),
      endDate: moment(endDateB).endOf('day'),
    },
  );
};

const getOverlappingPhases = (
  currentPhase: ProjectPhase,
  phases: ProjectPhase[],
): ProjectPhase[] => {
  const otherPhases = phases.filter(
    ({ projectPhaseId }) => projectPhaseId !== currentPhase.projectPhaseId,
  );

  const overlappingPhases = otherPhases.reduce(
    (rOverlappingPhases, phase) => {
      if (
        rOverlappingPhases
          .map(({ projectPhaseId }) => projectPhaseId)
          .includes(phase.projectPhaseId)
      ) {
        return rOverlappingPhases;
      }
      if (isPhaseOverlapping(currentPhase, phase)) {
        return [
          ...rOverlappingPhases,
          ...getOverlappingPhases(phase, otherPhases),
        ];
      }

      return rOverlappingPhases;
    },
    [currentPhase],
  );

  return uniqBy(overlappingPhases, 'projectPhaseId');
};

const groupPhasesByOverlapping = (
  phases: ProjectPhase[],
): Record<string, ProjectPhase[]> => {
  const checkedPhasesIds: number[] = [];
  const result = {};

  phases.forEach((phase) => {
    if (checkedPhasesIds.includes(phase.projectPhaseId)) {
      return;
    }
    const overlappingPhases = sortPhasesByStartDate(
      getOverlappingPhases(phase, phases),
    );

    result[overlappingPhases[0].startDate] = overlappingPhases;
    checkedPhasesIds.push(
      ...overlappingPhases.map(({ projectPhaseId }) => projectPhaseId),
    );
  });

  return result;
};

const getNonOverlappingPhases = (
  phasesNotBeOverlapped: ProjectPhase[],
  allPhases: ProjectPhase[],
  idsToBeOmitted: number[],
): ProjectPhase[] => {
  const nonOverlappingPhase = allPhases.find((searchingPhase) => {
    if (idsToBeOmitted.includes(searchingPhase.projectPhaseId)) {
      return false;
    }

    return phasesNotBeOverlapped.every(
      (phase) => !isPhaseOverlapping(phase, searchingPhase),
    );
  });

  return nonOverlappingPhase
    ? [
        ...getNonOverlappingPhases(
          [...phasesNotBeOverlapped, nonOverlappingPhase],
          allPhases,
          [...idsToBeOmitted, nonOverlappingPhase.projectPhaseId],
        ),
        nonOverlappingPhase,
      ]
    : [];
};

const getPhasesWithStyles = (
  groupedPhases: ProjectPhase[],
  groupKey: string,
): ProjectPhaseWithStyles[] => {
  const alreadyComputedPhaseIds: number[] = [];
  const arrangedPhases: (ProjectPhase & { rowIndex: number })[] = [];
  let numberOfRows = 0;

  groupedPhases.forEach((phase) => {
    if (alreadyComputedPhaseIds.includes(phase.projectPhaseId)) {
      return;
    }
    const nonOverlappingPhases = getNonOverlappingPhases(
      [phase],
      groupedPhases,
      [...alreadyComputedPhaseIds, phase.projectPhaseId],
    );

    if (nonOverlappingPhases.length > 0) {
      arrangedPhases.push({
        ...phase,
        rowIndex: numberOfRows,
      });
      nonOverlappingPhases.forEach((nonOverlappingPhase) => {
        arrangedPhases.push({
          ...nonOverlappingPhase,
          rowIndex: numberOfRows,
        });
        alreadyComputedPhaseIds.push(nonOverlappingPhase.projectPhaseId);
      });

      alreadyComputedPhaseIds.push(phase.projectPhaseId);
    } else {
      arrangedPhases.push({
        ...phase,
        rowIndex: numberOfRows,
      });
      alreadyComputedPhaseIds.push(phase.projectPhaseId);
    }
    numberOfRows += 1;
  });

  const phaseHeight = Math.floor(
    Math.min(
      PHASE_STYLE.defaultHeight,
      (PHASE_STYLE.totalAvailableSpace - numberOfRows - 1) / (numberOfRows - 1),
    ),
  );

  return arrangedPhases.map((phase) => {
    const top =
      phase.rowIndex === 0
        ? PHASE_STYLE.defaultTop
        : PHASE_STYLE.defaultTop +
          PHASE_STYLE.defaultHeight +
          phaseHeight * (phase.rowIndex - 1) +
          phase.rowIndex;

    return {
      ...phase,
      height: phaseHeight,
      top,
      groupKey,
    };
  });
};

export const arrangeProjectPhases = (
  projectPhases: ProjectPhase[] = [],
): ProjectPhaseWithStyles[] => {
  const sortedPhases = sortPhasesByStartDate(projectPhases);
  const phaseGroups = groupPhasesByOverlapping(sortedPhases);
  const arrangedPhases = Object.keys(phaseGroups)
    .map((groupKey) => getPhasesWithStyles(phaseGroups[groupKey], groupKey))
    .reduce((result, phases) => [...result, ...phases], []);

  return arrangedPhases;
};
